import { Component, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

import { getPropsFrom, xmlElementToString } from '../../../../utils/xmlParser';

enum ValuePart {
  Label = 0,
  Value = 1
}

interface OptionItemProperties {
  text: string;
  style: string;
  optionList: string;
  updateButtonDummy: string;
  listValues: string;
  orientation: string;
  selectionType: string;
  position: string;
}

@Component({
  selector: 'lfe-option-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './option-item-form.component.html',
  standalone: true,
  styleUrl: './option-item-form.component.css'
})
export class OptionItemFormComponent extends InteractiveItemComponent<OptionItemProperties> implements OnInit {
  formControl: FormControl<string | null> | undefined;
  properties: InteractiveItemProperties & OptionItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: '',
    optionList: '',
    updateButtonDummy: '',
    listValues: '',
    orientation: 'Horizontal',
    selectionType: 'SingleSelect',
    position: 'Left'
  };

  initialValue: string | null = null;

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `option_${this.currentId()}`);

  values = computed(() => (this.properties.listValues ? this.properties.listValues.split('|') : []));

  formControlName = computed(() => `${this.properties.selectionType}_${this.currentId()}`);

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    this.initialValue = this.properties.resultValue || '';
    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & OptionItemProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'] || '';
    const id = props['ID'] || '';
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const optionList = props['OptionList'] || '';
    const updateButtonDummy = props['UpdateButtonDummy'] || '';
    const listValues = props['ListValues'] || '';
    const orientation = props['Orientation'] || 'Horizontal';
    const selectionType = props['SelectionType'] || 'SingleSelect';
    const position = props['Position'] || 'Left';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      resultValue,
      resultUser,
      resultTime,
      text,
      style,
      optionList,
      updateButtonDummy,
      listValues,
      orientation,
      selectionType,
      position,
      isGenericRepeatItem
    };
  }

  getInputValue(value: string): string {
    return this.getValuePart(value, ValuePart.Value);
  }

  getInputLabel(value: string): string {
    return this.getValuePart(value, ValuePart.Label);
  }

  private getValuePart(value: string, part: ValuePart): string {
    const hasTilde = value.includes('~');
    if (hasTilde) {
      const maybeValue = value.split('~')[part]?.trim();
      if (maybeValue) {
        return maybeValue;
      }
    }
    return value;
  }

  onCheckboxChanged(event: Event, value: string): void {
    const input = event.target as HTMLInputElement;
    const inputValue = this.getInputValue(value);
    const currentValues = this.getSelectedValues();

    if (input.checked) {
      currentValues.add(inputValue);
    } else {
      currentValues.delete(inputValue);
    }

    const newValue = [...currentValues].join('|');
    this.formControl?.setValue(newValue);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.resultValue = newValue;
    this.properties.resultTime = new Date().toISOString();
    this.properties.resultUser = this.user().displayName;
  }

  onRadioChanged(event: Event, value: string): void {
    const input = event.target as HTMLInputElement;
    const inputValue = input.checked ? this.getInputValue(value) : '';
    this.formControl?.setValue(inputValue);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.resultValue = inputValue;
    this.properties.resultTime = new Date().toISOString();
    this.properties.resultUser = this.user().displayName;
  }

  isChecked(value: string): boolean {
    if (this.properties.selectionType === 'MultiSelect') {
      return this.getSelectedValues().has(this.getInputValue(value));
    }
    return this.formControl?.value === this.getInputValue(value);
  }

  private getSelectedValues(): Set<string> {
    const current = this.formControl?.value || '';
    return new Set(current.split('|').filter((v: string) => v.trim() !== ''));
  }

  getInputId(value: string): string {
    return `${this.properties.selectionType}_${this.properties.customId || this.properties.id}_${value}`;
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.properties.resultValue !== this.initialValue) {
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }
}
